if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.selectedProducts = new Set();
        this.selectedProductsOrder = []; // Maintains stable order of products
        this.concernFilters = [];
        this.productFilters = [];
        this.hairFilters = [];
        this.filterDict = {
          productFilters: {
            textId: 'product-filters-applied',
            labelId: 'product-type-label'
          },
          concernFilters: {
            textId: 'concern-filters-applied',
            labelId: 'concern-type-label'
          },
          hairFilters: {
            textId: 'hair-filters-applied',
            labelId: 'hair-type-label'
          }
        };

        // CSS class constants
        this.CSS_CLASSES = {
          FILTER_ACTIVE: ['bg-brand-secondary-200', 'text-white'],
          FILTER_INACTIVE: ['bg-white', 'text-brand-secondary-200']
        };
        this.debugPricing = true; // Toggle for price debugging
        this.init(); // Initial render of bundle contents
        // Initialize progress slots
        this.progressSlots = Array.from(this.querySelectorAll('[data-slot]'));
        console.log('Progress slots initialized:', this.progressSlots.length); // Debug log
        this.hasExistingBundle = false; // Add state tracking
        console.log(
          'BundleBuilder initialized, hasExistingBundle:',
          this.hasExistingBundle
        );

        // Add state change logging
        this.addEventListener('state-change', (e) => {
          console.log('Bundle state changed:', {
            old: e.detail.oldState,
            new: e.detail.newState
          });
        });

        this.loadSavedState();
        this.fetchProductIdsFromQueryParams();
      }

      // Inline helper: fetchConfig
      fetchConfig(type = 'json') {
        return {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: `application/${type}`
          }
        };
      }

      // Inline helper: publish (using custom events)
      publish(eventName, data) {
        document.dispatchEvent(
          new CustomEvent(eventName, { detail: data, bubbles: true })
        );
      }

      // Inline constants
      get PUB_SUB_EVENTS() {
        return {
          cartUpdate: 'cart-update',
          quantityUpdate: 'quantity-update',
          optionValueSelectionChange: 'option-value-selection-change',
          variantChange: 'variant-change',
          cartError: 'cart-error',
          purchaseTypeChange: 'purchase-type-change'
        };
      }

      async fetchProductIdsFromQueryParams() {
        const urlParams = new URLSearchParams(window.location.search);
        const routineParam = urlParams.get('routine');

        if (!routineParam) {
          return;
        }

        // If products were already loaded from sessionStorage, skip query param processing
        if (this.selectedProducts.size > 0) {
          return;
        }

        const productHandles = routineParam.split(',').map((id) => id.trim());

        if (productHandles.length === 0) {
          return;
        }

        try {
          await Promise.all(
            productHandles.map((productHandle) =>
              this.fetchAndAddProduct(productHandle)
            )
          );
        } catch (error) {
          console.error('Error loading products from query params:', error);
        }

        // Update UI after all products are loaded
        this.updateProgressBar();
        this.updateProductCardStates();

        const viewBundleBtn = this.querySelector('[data-bundle-toggle]');
        viewBundleBtn?.click();
      }

      async fetchAndAddProduct(productHandle) {
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          console.error(
            `Failed to fetch product ${productHandle}: ${response.status}`
          );
          return;
        }

        const productData = await response.json();
        const variant = productData.variants[0];
        if (!variant) {
          console.error(`No variants available for product ${productHandle}`);
          return;
        }

        const productInfo = {
          variantId: variant.id.toString(),
          productId: productData.id.toString(),
          baseProductId: productData.id.toString(),
          price: variant.price,
          title: productData.title,
          url: `/products/${productData.handle}?variant=${variant.id}`,
          imageUrl: productData.images?.[0] || productData.featured_image
        };

        // Validation checks
        if (
          Array.from(this.selectedProducts).find(
            (p) => p.baseProductId === productInfo.baseProductId
          )
        ) {
          console.log(`Product ${productData.title} already in bundle, skipping`);
          return;
        }
        if (this.selectedProducts.size >= 5) {
          console.warn(`Bundle limit reached, cannot add ${productData.title}`);
          return;
        }

        this.selectedProducts.add(productInfo);
        this.selectedProductsOrder.push(productInfo);

        const wrapper = this.findProductWrapper(productInfo);
        if (wrapper) {
          this.selectProductInUI(wrapper, productInfo);
        }

        this.updateBundleState();
      }

      findProductWrapper(productInfo) {
        // Try to find by baseProductId first
        let wrapper = this.productsContainer?.querySelector(
          `[data-base-product-id="${productInfo.baseProductId}"]`
        );

        // If not found, search by variant-id
        if (!wrapper) {
          const matchingVariantElement = this.productsContainer?.querySelector(
            `product-variant[variant-id="${productInfo.variantId}"]`
          );
          if (matchingVariantElement) {
            wrapper = matchingVariantElement.closest('.product-card-wrapper');
            if (wrapper) {
              productInfo.baseProductId = wrapper.getAttribute(
                'data-base-product-id'
              );
            }
          }
        }

        return wrapper;
      }

      selectProductInUI(wrapper, productInfo) {
        const card = wrapper.querySelector('card-product');
        if (!card) return;

        // Select the variant in the variant selector
        this.selectVariantOption(card, productInfo);

        // Mark card as selected
        card.classList.add('is-selected');
        const submitButton = card.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.textContent = 'Remove from Bundle';
          submitButton.classList.add(
            'bg-black',
            'text-white',
            'hover:bg-white',
            'hover:text-black'
          );
        }

        // Disable other variant options
        this.disableVariantSelectors(
          productInfo.baseProductId,
          productInfo.variantId
        );
      }

      selectVariantOption(card, productInfo) {
        const variantSelector = card.querySelector('variant-select-card');
        if (!variantSelector) return;

        const matchingVariant = variantSelector.querySelector(
          `product-variant[variant-id="${productInfo.variantId}"]`
        );
        if (!matchingVariant) return;

        // Update variant selection UI
        variantSelector
          .querySelectorAll('product-variant')
          .forEach((v) => v.classList.remove('variant-selected-size'));
        matchingVariant.classList.add('variant-selected-size');

        // Update form inputs
        const selectedSizeInput = variantSelector.querySelector(
          'input[name="selected-variant-option"]'
        );
        const selectedIdInput = variantSelector.querySelector(
          'input[name="selected-variant-id"]'
        );
        if (selectedSizeInput) selectedSizeInput.value = productInfo.url;
        if (selectedIdInput) selectedIdInput.value = productInfo.variantId;

        const variantIdInput = card.querySelector(
          'product-form form input[name="id"]'
        );
        if (variantIdInput) variantIdInput.value = productInfo.variantId;
      }

      loadSavedState() {
        const savedState = sessionStorage.getItem('bundleBuilderState');
        console.log('Loading saved bundle state:', savedState);

        if (savedState) {
          const state = JSON.parse(savedState);
          console.log('Parsed state:', state);

          this.selectedProductsOrder = state.products || [];
          this.selectedProducts = new Set(this.selectedProductsOrder);
          console.log(
            'Restored selectedProducts Set:',
            Array.from(this.selectedProducts)
          );

          if (this.selectedProducts.size > 0) {
            console.log('Found saved products, updating UI...');

            // Update progress bar
            this.updateProgressBar();

            // Update product cards to match their "added to bundle" state
            this.selectedProducts.forEach((productInfo) => {
              console.log('Processing saved product:', productInfo);

              // Find the product wrapper using baseProductId
              const productWrapper = this.productsContainer.querySelector(
                `.product-card-wrapper[data-base-product-id="${productInfo.baseProductId}"]`
              );
              console.log('Found product wrapper:', productWrapper);

              if (productWrapper) {
                const cardProduct = productWrapper.querySelector('card-product');
                console.log('Found card product:', cardProduct);

                if (cardProduct) {
                  cardProduct.classList.add('is-selected');
                  const submitButton = cardProduct.querySelector(
                    'button[type="submit"]'
                  );
                  if (submitButton) {
                    submitButton.textContent = 'Remove from Bundle';
                    submitButton.classList.add(
                      'bg-black',
                      'text-white',
                      'hover:bg-white',
                      'hover:text-black'
                    );
                  }

                  console.log('Calling disableVariantSelectors with:', {
                    baseProductId: productInfo.baseProductId,
                    variantId: productInfo.variantId
                  });

                  this.disableVariantSelectors(
                    productInfo.baseProductId,
                    productInfo.variantId
                  );
                }
              } else {
                console.warn('Could not find product wrapper for:', productInfo);
              }
            });

            // Update card states based on current selection count
            this.updateProductCardStates();
          } else {
            console.log('No saved products found');
          }
        } else {
          console.log('No saved state found in sessionStorage');
        }
      }

      saveState() {
        const state = {
          products: this.selectedProductsOrder
        };

        sessionStorage.setItem('bundleBuilderState', JSON.stringify(state));
        console.log('State saved to sessionStorage');

        // Verify save
        const savedState = sessionStorage.getItem('bundleBuilderState');
        console.log('Verified saved state:', savedState);
      }

      init() {
        // Log raw collection data if available
        const collectionData = JSON.parse(
          document.getElementById('collection-data')?.textContent || '{}'
        );
        if (this.debugPricing) {
          console.group('Bundle Builder - Collection Data');
          console.log('Raw collection data:', collectionData);

          // Log specific price data for each product
          collectionData.products?.forEach((product) => {
            console.group(`Product: ${product.title}`);
            console.log('Regular price:', product.price);
            console.log('Compare at price:', product.compare_at_price);
            console.log('Variants:', product.variants);
            console.log('Tags:', product.tags);
            console.log('Type:', product.type);
            console.groupEnd();
          });
          console.groupEnd();
        }

        // Filter controls
        this.concernFilter = this.querySelector('[data-filter-concern]');
        this.productTypeFilter = this.querySelector('[data-filter-product-type]');
        this.hairTypeFilter = this.querySelector('[data-filter-hair-type]');

        // Add event listeners for all filters
        const filters = [
          {
            element: this.concernFilter,
            selector: '[data-filter-concern]',
            key: 'concernFilters'
          },
          {
            element: this.productTypeFilter,
            selector: '[data-filter-product-type]',
            key: 'productFilters'
          },
          {
            element: this.hairTypeFilter,
            selector: '[data-filter-hair-type]',
            key: 'hairFilters'
          }
        ];

        filters.forEach(({ element, selector, key }) => {
          if (element) {
            element.addEventListener('change', () => {
              this.onFilterChange(selector, key);
            });

            const detailsElement = element.querySelector('details');
            if (detailsElement) {
              detailsElement.addEventListener('toggle', () => {
                this.handleDetailsToggle(detailsElement, key);
              });
            }
          }
        });

        // Product cards container
        this.productsContainer = this.querySelector('[data-products-container]');

        // Price summary elements
        this.subtotalElement = this.querySelector('[data-subtotal]');
        this.discountElement = this.querySelector('[data-discount]');
        this.totalElement = this.querySelector('[data-total]');
        this.totalButtonElement = this.querySelector('[data-total-button]');

        // Savings banner element
        this.savingsBanner = this.querySelector('[data-savings-banner]');

        // Add to cart button
        this.addToCartButton = this.querySelector('[data-bundle-submit]');
        if (this.addToCartButton) {
          // Ensure button starts disabled
          this.addToCartButton.disabled = true;
          this.addToCartButton.addEventListener('click', () => {
            this.handleAddToCart();
          });
        }

        // Initialize product cards
        this.initializeProductCards();

        // Initialize product info modal
        this.initializeProductModal();

        // Initialize mobile bundle toggle
        this.initializeMobileToggle();

        // Check for existing bundles in cart
        this.checkExistingBundles();

        // Initial state update
        this.updateBundleState();

        // Check for existing discounts
        this.checkForExistingDiscounts().then((hasDiscount) => {
          if (hasDiscount) {
            console.log('Existing discount detected');
          }
        });
      }

      async checkExistingBundles() {
        try {
          const response = await fetch(window.routes.cart_url + '.js');
          const cart = await response.json();

          const hasBundleItems = cart.items.some(
            (item) => item.properties?._bundle
          );

          if (hasBundleItems) {
            this.disableBundleBuilder();
          }
        } catch (error) {
          console.error('Error checking for existing bundles:', error);
        }
      }

      disableBundleBuilder() {
        console.log('disableBundleBuilder called, current state:', {
          hasExistingBundle: this.hasExistingBundle,
          submitButtonExists: !!this.querySelector('[data-bundle-submit]'),
          errorExists: !!this.querySelector('[data-bundle-error]')
        });
        this.hasExistingBundle = true; // Set state when disabled due to cart validation

        // Hide the savings banner and submit button
        if (this.savingsBanner) {
          this.savingsBanner.style.display = 'none';
        }

        if (this.addToCartButton) {
          this.addToCartButton.style.display = 'none';
        }

        // Create error message if it doesn't exist
        if (!this.querySelector('[data-bundle-error]')) {
          const errorDiv = document.createElement('div');
          errorDiv.setAttribute('data-bundle-error', '');
          errorDiv.classList.add(
            'w-full',
            'py-3',
            'px-4',
            'text-xs',
            'text-center',
            'bg-black',
            'text-white'
          );
          errorDiv.innerHTML = `
            <div class="font-bold">You already have a bundle in your cart.</div>
            <div class="mt-1">To correctly calculate discounts, we only offer one bundle per order.</div>
          `;

          // Insert where the savings banner would be
          const container = this.addToCartButton?.parentNode;
          if (container) {
            container.insertBefore(errorDiv, container.firstChild);
          }
        }
      }

      enableBundleBuilder() {
        console.log('enableBundleBuilder called, current state:', {
          hasExistingBundle: this.hasExistingBundle,
          selectedProductsCount: this.selectedProducts.size,
          submitButtonExists: !!this.addToCartButton,
          submitButtonDisabled: this.addToCartButton?.disabled
        });
        this.hasExistingBundle = false; // Reset state when enabled

        // Show the savings banner and submit button again
        if (this.savingsBanner) {
          this.savingsBanner.style.display = '';
        }

        if (this.addToCartButton) {
          this.addToCartButton.style.display = '';
          // Only disable the button if there aren't any items in progress slots
          this.addToCartButton.disabled = this.selectedProducts.size === 0;
        }

        // Remove error message if it exists
        const errorDiv = this.querySelector('[data-bundle-error]');
        if (errorDiv) {
          errorDiv.remove();
        }
      }

      /**
       *
       * @param {string} dataAttribute attribute that is used to grab all of the checkbox for that container
       * @param {string} stateKey string that represents the key that we need to target in order to properly update the state of the bundle builder
       */
      onFilterChange(dataAttribute, stateKey) {
        let newState = [];
        const checkboxContainer = this.querySelector(dataAttribute);
        const checkedRadioInputs = checkboxContainer.querySelectorAll(
          'input[type="checkbox"]:checked'
        );

        checkedRadioInputs.forEach((input) => {
          newState.push(input.value);
        });

        this[stateKey] = [...newState];
        this.handleFilter();
        this.handleTextChange(stateKey);
      }

      /**
       *
       * @param {string} stateKey key that is used to determine which text element to update on the DOM.
       */
      handleTextChange(stateKey) {
        let filterState = this[stateKey];
        let filterText = document.getElementById(this.filterDict[stateKey].textId);

        if (filterState.length === 1) {
          filterText.textContent = filterState[0];
        } else if (filterState.length > 1) {
          filterText.textContent = `${filterState.length} TYPES`;
        } else {
          filterText.textContent = 'ANY';
        }
      }

      /**
       * Handle details/summary toggle events and apply conditional styling
       * @param {HTMLDetailsElement} detailsElement
       * @param {string} stateKey
       */
      handleDetailsToggle(detailsElement, stateKey) {
        const filterState = this[stateKey];
        const hasFilters = filterState.length > 0;

        // If details is closed and filters are applied, update styling
        if (!detailsElement.open && hasFilters) {
          // Add closed with filters styling
          detailsElement.classList.add(...this.CSS_CLASSES.FILTER_ACTIVE);
          detailsElement.classList.remove(...this.CSS_CLASSES.FILTER_INACTIVE);
        } else {
          // Reset to default styling (open or no filters)
          detailsElement.classList.remove(...this.CSS_CLASSES.FILTER_ACTIVE);
          detailsElement.classList.add(...this.CSS_CLASSES.FILTER_INACTIVE);
        }
      }

      handleFilter() {
        const hairTypeValue = this.hairTypeFilter?.value || '';

        console.log('Filter values:', {
          concernFilters: this.concernFilters,
          productFilters: this.productFilters,
          hairTypeValue
        });

        this.productsContainer
          .querySelectorAll('.product-card-wrapper')
          .forEach((wrapper) => {
            const productConcern = wrapper.dataset.concern || '';
            const productType = wrapper.dataset.productType || '';
            const hairType = wrapper.dataset.hairType || '';

            console.log('Product data:', {
              productConcern,
              productType,
              hairType
            });

            // Check if product matches concern filters
            let matchesConcern;
            if (this.concernFilters.length) {
              matchesConcern = this.concernFilters.some((concern) =>
                productConcern.toLowerCase().includes(concern.toLowerCase())
              );
            } else {
              matchesConcern = true;
            }

            // Check if product matches product type filters
            let matchesProductType;
            if (this.productFilters.length) {
              // Parse product type using regex to handle compound strings
              const productTypes =
                productType.match(/[A-Z][a-z]+(?:[\s-][A-Z][a-z]+)*/g) || [];

              // Check if any of the parsed product types match any of the selected filters
              matchesProductType = this.productFilters.some((selectedFilter) =>
                productTypes.some(
                  (type) => type.toLowerCase() === selectedFilter.toLowerCase()
                )
              );

              console.log('Product type parsing:', {
                original: productType,
                parsed: productTypes,
                selectedFilters: this.productFilters,
                matches: matchesProductType
              });
            } else {
              matchesProductType = true;
            }

            let matchesHairType;
            if (this.hairFilters.length) {
              matchesHairType = this.hairFilters.some((selectedFilter) =>
                hairType.toLowerCase().includes(selectedFilter.toLowerCase())
              );
            } else {
              matchesHairType = true;
            }

            console.log('Match results:', {
              matchesConcern,
              matchesProductType
            });

            // Show product only if it matches all active filters
            wrapper.classList.toggle(
              'hidden',
              !(matchesConcern && matchesProductType && matchesHairType)
            );
          });
      }

      // More sophisticated price handling could include:
      getPriceDetails(product, variant) {
        // 1. Set/Kit Detection
        const isSet = product.tags?.some(
          (tag) =>
            tag.toLowerCase().includes('set') || tag.toLowerCase().includes('kit')
        );

        // 2. Sale Detection
        const hasDiscount = variant.compare_at_price > variant.price;

        // 3. Volume Discount Detection
        const hasVolumeDiscount = product.tags?.some((tag) =>
          tag.toLowerCase().includes('volume-discount')
        );

        // 4. Subscription Price Detection (if applicable)
        const subscriptionPrice = variant.selling_plan_allocations?.[0]?.price;

        // 5. Final Price Calculation
        let finalPrice = variant.price; // Base price
        let priceType = 'regular';

        if (isSet) {
          priceType = 'set';
          // Use set price directly, ignoring compare_at
          finalPrice = variant.price;
        } else if (hasDiscount) {
          priceType = 'sale';
          finalPrice = variant.price;
        } else if (hasVolumeDiscount) {
          priceType = 'volume';
          // Apply volume discount logic if needed
        }

        if (this.debugPricing) {
          console.group(`Price Details for ${product.title}`);
          console.log('Is Set:', isSet);
          console.log('Has Discount:', hasDiscount);
          console.log('Has Volume Discount:', hasVolumeDiscount);
          console.log('Regular Price:', variant.price);
          console.log('Compare At Price:', variant.compare_at_price);
          console.log('Final Price:', finalPrice);
          console.log('Price Type:', priceType);
          console.groupEnd();
        }

        return {
          finalPrice,
          originalPrice: variant.compare_at_price || variant.price,
          priceType,
          subscriptionPrice,
          isSet,
          hasDiscount
        };
      }

      initializeMobileToggle() {
        const bundleProgress = this.querySelector('[data-bundle-progress]');
        const viewBundleBtn = this.querySelector('[data-bundle-toggle]');
        const hideBundleBtn = this.querySelector('[data-bundle-hide]');

        if (viewBundleBtn && bundleProgress) {
          viewBundleBtn.addEventListener('click', () => {
            bundleProgress.classList.remove('translate-y-full');
            bundleProgress.classList.add('translate-y-0');
          });
        }

        if (hideBundleBtn && bundleProgress) {
          hideBundleBtn.addEventListener('click', () => {
            bundleProgress.classList.add('translate-y-full');
            bundleProgress.classList.remove('translate-y-0');
          });
        }
      }

      initializeProductModal() {
        // Get all modals
        this.modals = this.querySelectorAll('[data-product-modal]');
        if (!this.modals.length) return;

        // Setup close handlers for all modals
        this.modals.forEach((modal) => {
          const closeBtn = modal.querySelector('[data-modal-close]');
          const backdrop = modal.querySelector('[data-modal-backdrop]');
          const addBundleBtn = modal.querySelector('[data-modal-add-bundle]');

          if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeModal(modal));
          }

          if (backdrop) {
            backdrop.addEventListener('click', (e) => {
              // Check if click is on backdrop container or its immediate flex wrapper
              // but not on the modal content (white box) or its children
              const modalContent = backdrop.querySelector(
                '.bg-white.rounded-lg.shadow-xl'
              );
              if (
                modalContent &&
                !modalContent.contains(e.target) &&
                (e.target === backdrop ||
                  e.target.classList.contains('flex') ||
                  e.target.classList.contains('items-end'))
              ) {
                this.closeModal(modal);
              }
            });
          }

          // Add to bundle handler
          if (addBundleBtn) {
            addBundleBtn.addEventListener('click', () => {
              const productId = addBundleBtn.dataset.modalAddBundle;

              this.addProductToBundle(productId);
              if (typeof dataLayer !== 'undefined') {
                dataLayer.push({
                  event: 'hairstory_product_modal_add_to_bundle_click',
                  product_id: productId
                });
              }
              this.closeModal(modal);
            });
          }
        });

        // ESC key handler for all modals
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const openModal = Array.from(this.modals).find(
              (modal) => !modal.classList.contains('hidden')
            );
            if (openModal) {
              this.closeModal(openModal);
            }
          }
        });

        // Add click handlers to product cards
        this.productsContainer
          .querySelectorAll('.product-card-wrapper')
          .forEach((wrapper) => {
            const productInfoTriggers = wrapper.querySelectorAll(
              '[data-product-info-trigger]'
            );

            productInfoTriggers.forEach((trigger) => {
              trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const productId = wrapper.dataset.baseProductId;
                this.showModal(productId);
              });
            });
          });
      }

      showModal(productId) {
        // Find the modal for this product
        const modal = Array.from(this.modals).find(
          (modal) => modal.dataset.productModal === productId
        );

        if (modal) {
          // Show modal
          modal.classList.remove('hidden');
          document.body.style.overflow = 'hidden';
        }
      }

      closeModal(modal) {
        console.log('calling close modal');
        modal.classList.add('hidden');
        document.body.style.overflow = '';
      }

      addProductToBundle(productId) {
        // Find the corresponding product form and trigger the bundle addition
        const wrapper = this.productsContainer.querySelector(
          `[data-base-product-id="${productId}"]`
        );

        if (wrapper) {
          const submitButton = wrapper.querySelector('button[type="submit"]');
          if (submitButton && !submitButton.disabled) {
            submitButton.click();
          }
        }
      }

      initializeProductCards() {
        this.productsContainer
          .querySelectorAll('product-form')
          .forEach((productForm) => {
            const card = productForm.closest('card-product');
            const wrapper = card?.closest('.product-card-wrapper');
            if (!wrapper) return;

            try {
              // Prevent direct form submission
              productForm.addEventListener(
                'submit',
                (evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  this.handleProductSelection(evt);
                },
                true
              );

              // Get initial product data and set initial price
              const variantSelector = card.querySelector('variant-select-card');
              if (variantSelector) {
                // Get the initially selected variant
                const selectedVariant = variantSelector.querySelector(
                  '.variant-selected-size'
                );
                if (selectedVariant) {
                  const productHandle = selectedVariant
                    .getAttribute('variant-value')
                    .split('?')[0];
                  const variantId = selectedVariant.getAttribute('variant-id');

                  // Set initial price
                  fetch(`${productHandle}.js`)
                    .then((response) => response.json())
                    .then((productData) => {
                      const variant = productData.variants.find(
                        (v) => v.id.toString() === variantId
                      );
                      if (variant) {
                        card.dataset.bundlePrice = variant.price;
                        console.log('Set initial price:', {
                          variantId,
                          price: variant.price
                        });
                      }
                    })
                    .catch((error) => {
                      console.error('Error setting initial price:', error);
                    });
                }

                // Watch for variant selection changes
                const observer = new MutationObserver(async (mutations) => {
                  mutations.forEach(async (mutation) => {
                    if (
                      mutation.type === 'attributes' &&
                      mutation.attributeName === 'class'
                    ) {
                      const selectedVariant = variantSelector.querySelector(
                        '.variant-selected-size'
                      );
                      if (selectedVariant) {
                        const productHandle = selectedVariant
                          .getAttribute('variant-value')
                          .split('?')[0];
                        const variantId =
                          selectedVariant.getAttribute('variant-id');

                        console.log('Fetching variant data:', {
                          productHandle,
                          variantId
                        });

                        try {
                          // Fetch product data directly like variant-select-card does
                          const response = await fetch(`${productHandle}.js`);
                          const productData = await response.json();
                          const variant = productData.variants.find(
                            (v) => v.id.toString() === variantId
                          );

                          console.log('Found variant:', variant);

                          if (variant) {
                            // Update bundle price with the variant's actual price
                            card.dataset.bundlePrice = variant.price;

                            // Update selection if product is already in bundle
                            const existingSelection = Array.from(
                              this.selectedProducts
                            ).find(
                              (p) =>
                                p.baseProductId === wrapper.dataset.baseProductId
                            );

                            if (existingSelection) {
                              // Get the product image
                              const productImage = card.querySelector(
                                '[data-product-info-trigger] img.aspect-square, a > img.aspect-square:not(.badge-image)'
                              );

                              const newProductInfo = {
                                variantId: variantId,
                                productId: productData.id,
                                baseProductId: wrapper.dataset.baseProductId,
                                price: variant.price,
                                title: productData.title, // This will be the new product's title
                                url: selectedVariant.getAttribute('variant-value'),
                                imageUrl: productImage?.src
                              };

                              this.selectedProducts.delete(existingSelection);
                              this.selectedProducts.add(newProductInfo);

                              // Update in place in the order array
                              const orderIndex =
                                this.selectedProductsOrder.findIndex(
                                  (p) =>
                                    p.baseProductId ===
                                    wrapper.dataset.baseProductId
                                );
                              if (orderIndex !== -1) {
                                this.selectedProductsOrder[orderIndex] =
                                  newProductInfo;
                              }

                              // Update button text with new price
                              const submitButton = card.querySelector(
                                'button[type="submit"]'
                              );
                              if (submitButton) {
                                submitButton.textContent = 'Remove from Bundle';
                              }

                              this.updateProgressBar(); // Update the UI to show new title
                            } else {
                              // Product is NOT in bundle, but variant changed - update button price
                              const submitButton = card.querySelector(
                                'button[type="submit"]'
                              );
                              if (
                                submitButton &&
                                !card.classList.contains('is-selected')
                              ) {
                                // Clear any existing timeout to prevent multiple rapid updates
                                if (card.priceUpdateTimeout) {
                                  clearTimeout(card.priceUpdateTimeout);
                                }

                                card.priceUpdateTimeout = setTimeout(() => {
                                  // Get the currently selected variant from DOM instead of using stale mutation data
                                  const currentSelectedVariant =
                                    variantSelector.querySelector(
                                      '.variant-selected-size'
                                    );
                                  const currentVariantId =
                                    currentSelectedVariant?.getAttribute(
                                      'variant-id'
                                    );

                                  if (currentVariantId === variantId) {
                                    // Only update if this is still the selected variant
                                    const formattedMoney =
                                      window.Shopify.formatMoney(variant.price);
                                    submitButton.textContent = `Add to bundle — ${formattedMoney}`;
                                  }
                                  card.priceUpdateTimeout = null;
                                }, 50);
                              }
                            }
                          }
                        } catch (error) {
                          console.error('Error fetching variant data:', error);
                        }
                      }
                    }
                  });
                });

                // Observe variant elements for selection changes
                variantSelector
                  .querySelectorAll('product-variant')
                  .forEach((variant) => {
                    observer.observe(variant, {
                      attributes: true,
                      attributeFilter: ['class']
                    });
                  });
              }
            } catch (error) {
              console.error('Error initializing product card:', error);
            }
          });
      }

      handleProductSelection(event) {
        event.preventDefault();
        const productForm = event.target.closest('product-form');
        const card = productForm.closest('card-product');
        const wrapper = card?.closest('.product-card-wrapper');
        if (!wrapper) return;

        const formData = new FormData(productForm.querySelector('form'));
        const variantId = formData.get('id');
        const priceInCents = parseInt(card.dataset.bundlePrice, 10);
        const baseProductId = wrapper.dataset.baseProductId;

        // Get main product image, being very specific to exclude badges
        const productImage = card.querySelector(
          '[data-product-info-trigger] img.aspect-square, a > img.aspect-square:not(.badge-image)'
        );

        const productInfo = {
          variantId,
          productId: card.dataset.productId,
          baseProductId,
          price: priceInCents,
          title: card.querySelector('h4')?.textContent || 'Product',
          url: card.querySelector('a')?.href || '',
          imageUrl: productImage?.src
        };

        // Toggle selection - first check if this exact variant is selected
        let existingSelection = Array.from(this.selectedProducts).find(
          (p) => p.variantId === variantId
        );

        // If not found by variantId, check if any variant of this base product is selected
        if (!existingSelection) {
          // Analytics call to add a product to bundle
          if (typeof dataLayer !== 'undefined') {
            dataLayer.push({
              event: 'hairstory_add_product_to_bundle',
              product_id: baseProductId
            });
          }
          existingSelection = Array.from(this.selectedProducts).find(
            (p) => p.baseProductId === baseProductId
          );
        }

        if (existingSelection) {
          // Analytics call to remove product from bundle
          if (typeof dataLayer !== 'undefined') {
            dataLayer.push({
              event: 'hairstory_remove_product_from_bundle_grid_click',
              product_id: baseProductId
            });
          }
          this.selectedProducts.delete(existingSelection);
          // Remove from order array
          this.selectedProductsOrder = this.selectedProductsOrder.filter(
            (p) => p.baseProductId !== baseProductId
          );
          card.classList.remove('is-selected');
          card
            .querySelector('button[type="submit"]')
            .classList.remove(
              'bg-black',
              'text-white',
              'hover:bg-white',
              'hover:text-black'
            );
          // Let the mutation observer handle all price updates
          // Don't update button text here to avoid conflicts with mutation observer

          // Re-enable all variant selectors for this base product
          this.enableVariantSelectors(baseProductId);

          // Update card states when we now have less than 5 products
          this.updateProductCardStates();
        } else {
          // Check if we've reached the 5 product limit
          if (this.selectedProducts.size >= 5) {
            return;
          }

          if (priceInCents > 0) {
            this.selectedProducts.add(productInfo);
            // Add to order array
            this.selectedProductsOrder.push(productInfo);
            card.classList.add('is-selected');
            card
              .querySelector('button[type="submit"]')
              .classList.add(
                'bg-black',
                'text-white',
                'hover:bg-white',
                'hover:text-black'
              );
            card.querySelector('button[type="submit"]').textContent =
              'Remove from Bundle';

            // Disable variant selectors for other sizes of this product
            this.disableVariantSelectors(baseProductId, variantId);

            // Update card states when we reach 5 products
            if (this.selectedProducts.size === 5) {
              this.updateProductCardStates();
            }
          } else {
            console.error('Invalid price in data attribute:', card.dataset);
            return;
          }
        }

        this.updateBundleState();
        this.updateProgressBar();
      }

      getDiscountRate(itemCount) {
        if (itemCount >= 4) return 0.2;
        if (itemCount === 3) return 0.15;
        if (itemCount === 2) return 0.1;
        return 0;
      }

      calculateTotals() {
        const subtotal = this.selectedProductsOrder.reduce(
          (sum, product) => sum + product.price,
          0
        );
        const discountRate = this.getDiscountRate(this.selectedProducts.size);
        const discount = subtotal * discountRate;

        return {
          subtotal,
          discount,
          total: subtotal - discount
        };
      }

      updateBundleState() {
        const itemCount = this.selectedProducts.size;
        const discountRate = this.getDiscountRate(itemCount);

        const discountMessage = this.querySelector('[data-discount-message]');
        if (discountMessage) {
          discountMessage.textContent =
            itemCount === 0
              ? 'Add items to your bundle to receive a discount!'
              : `Current Discount: ${discountRate * 100}%`;
        }

        if (this.addToCartButton) {
          const hasOneProduct = itemCount >= 1;
          const shouldDisableButton = !hasOneProduct || this.hasExistingBundle;
          this.addToCartButton.disabled = shouldDisableButton;
        }
      }

      async handleAddToCart() {
        if (this.selectedProducts.size === 0) return;

        const items = this.selectedProductsOrder.map((product) => ({
          id: product.variantId,
          quantity: 1,
          properties: {
            _bundle: 'true'
          }
        }));

        try {
          const formData = new FormData();

          // Add items array with bundle property
          items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            formData.append(`items[${index}][properties][_bundle]`, 'true');
          });

          formData.append('sections', 'v2-cart-drawer,v2-cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);

          const response = await fetch(window.routes.cart_add_url, {
            ...this.fetchConfig('javascript'),
            body: formData
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          // After successful bundle add, disable the builder
          this.disableBundleBuilder();

          // analytics for adding the bundle to cart
          if (typeof dataLayer !== 'undefined') {
            dataLayer.push({
              event: 'hairstory_add_bundle_to_cart'
            });
          }
          // Update v2 cart drawer
          const v2CartDrawer = document.querySelector('cart-drawer');
          if (v2CartDrawer) {
            v2CartDrawer.renderSections(data.sections, 'add', 'Custom Bundle');
            v2CartDrawer.open();

            // On mobile, hide the bundle progress track after adding to cart
            const bundleProgress = this.querySelector('[data-bundle-progress]');
            if (bundleProgress) {
              bundleProgress.classList.add('translate-y-full');
              bundleProgress.classList.remove('translate-y-0');
            }
          }

          // Products remain selected but builder is disabled until bundle is removed from cart
          this.updateBundleState();

          // Publish cart update event
          this.publish(this.PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-builder',
            cartData: data
          });
        } catch (error) {
          console.error('Error adding bundle to cart:', error);
          this.publish(this.PUB_SUB_EVENTS.cartError, {
            source: 'bundle-builder',
            errors: error.message,
            message: 'Error adding bundle to cart'
          });
        }
      }

      updateProgressBar() {
        console.log('updateProgressBar called, current state:', {
          hasExistingBundle: this.hasExistingBundle,
          selectedProductsCount: this.selectedProducts.size,
          submitButtonExists: !!this.addToCartButton,
          submitButtonDisabled: this.addToCartButton?.disabled
        });

        if (!this.progressSlots?.length) {
          console.error('No progress slots found');
          return;
        }

        const products = this.selectedProductsOrder;
        const productCount = products.length;

        // Get all slot elements
        const slots = this.querySelectorAll('[data-slot]');
        if (!slots.length) {
          console.error('No slots found');
          return;
        }

        // Reset all slots to their original state first
        slots.forEach((slot, index) => {
          const slotNumber = index + 1;

          // Clear any existing product content
          slot.innerHTML =
            slotNumber > 1
              ? `
            <svg class='mr-2 w-3 h-3.5 text-gray-400' viewBox='0 0 12 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d="M3.08333 6.41676V4.08342C3.08261 3.36012 3.35066 2.66234 3.83547 2.12556C4.32027 1.58878 4.98723 1.25128 5.70688 1.17859C6.42652 1.1059 7.1475 1.3032 7.72986 1.73219C8.31222 2.16118 8.7144 2.79125 8.85833 3.50009M1.91667 6.41676H10.0833C10.7277 6.41676 11.25 6.93909 11.25 7.58342V11.6668C11.25 12.3111 10.7277 12.8334 10.0833 12.8334H1.91667C1.27233 12.8334 0.75 12.3111 0.75 11.6668V7.58342C0.75 6.93909 1.27233 6.41676 1.91667 6.41676Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h4 class="text-sm text-gray-500">
              ${
                slotNumber === 2
                  ? '10% off'
                  : slotNumber === 3
                    ? '15% off'
                    : '20% off'
              }
            </h4>
          `
              : `
            <h4 class="text-sm text-gray-500">Add a product</h4>
          `;

          // Reset styling to placeholder state
          slot.className =
            'relative flex items-center w-full h-[80px] border border-dashed border-gray-300 bg-white p-3';
        });

        // Fill slots with selected products
        products.forEach((product, index) => {
          if (index >= slots.length) return; // Don't exceed available slots

          const slot = slots[index];

          // Calculate discount for this product
          const discountRate = this.getDiscountRate(productCount);
          const originalPrice = product.price;
          const discountedPrice = originalPrice * (1 - discountRate);
          const formattedOriginalPrice = window.Shopify.formatMoney(originalPrice);
          const formattedDiscountedPrice =
            window.Shopify.formatMoney(discountedPrice);

          // Update slot styling to filled state (remove padding when filled)
          slot.className =
            'relative flex items-center w-full h-[80px] border border-black bg-white';

          // Clear placeholder content and add product content
          slot.innerHTML = `
            <div class="flex-shrink-0 w-[80px] h-full">
              <img src="${product.imageUrl}" alt="${product.title}" class="object-cover w-full h-full">
            </div>
            <div class="flex-grow px-3">
              <h4 class="text-sm font-medium uppercase max-w-[140px]">${product.title}</h4>
              <div data-product-prices>
                ${
                  discountRate > 0
                    ? `<span class="text-xs line-through text-gray-500">${formattedOriginalPrice}</span>
                     <span class="text-xs font-semibold ml-1">${formattedDiscountedPrice}</span>`
                    : `<span class="text-xs font-semibold">${formattedOriginalPrice}</span>`
                }
              </div>
            </div>
            <button type="button" class="flex absolute top-0 right-0 justify-center items-center p-1 w-8 h-8 bg-white border-b border-l border-black hover:bg-gray-50 hover:cursor-pointer" data-remove-item>
              <svg class='w-4 h-4' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d="M11 1L1 11M1 1L11 11" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
          `;

          // Add click handler for remove button
          const removeButton = slot.querySelector('[data-remove-item]');
          if (removeButton) {
            removeButton.addEventListener('click', () => {
              console.log('Remove button clicked for product:', product);

              // Try to find matching product card in the collection
              let foundCard = false;
              const productCards =
                this.productsContainer.querySelectorAll('card-product');

              productCards.forEach((card) => {
                const form = card.querySelector('product-form form');
                const variantInput = form?.querySelector('input[name="id"]');

                if (variantInput?.value === product.variantId) {
                  const submitButton = card.querySelector('button[type="submit"]');
                  if (submitButton) {
                    console.log(
                      'Found matching product card, clicking remove button'
                    );
                    submitButton.click();
                    foundCard = true;
                  }
                }
              });

              // If product card not found (e.g., added via query param but not in collection),
              // manually remove it from the bundle
              if (!foundCard && this.selectedProducts.has(product)) {
                this.selectedProducts.delete(product);

                this.selectedProductsOrder = this.selectedProductsOrder.filter(
                  (p) => p.variantId !== product.variantId
                );

                this.updateProgressBar();
                this.updateBundleState();
                this.updateProductCardStates();

                // Analytics call to remove product from bundle
                if (typeof dataLayer !== 'undefined') {
                  dataLayer.push({
                    event: 'hairstory_remove_product_from_bundle_progress_click',
                    product_id: product.baseProductId
                  });
                }
              }
            });
          }
        });

        // Update heading based on product count
        const countSpan = this.querySelector('[data-bundle-count]');
        const footerCountSpan = this.querySelector('[data-bundle-count-footer]');

        if (countSpan) {
          countSpan.textContent = `(${productCount})`;
        }

        if (footerCountSpan) {
          footerCountSpan.textContent = `(${productCount})`;
        }

        // Update savings banner
        if (this.savingsBanner) {
          if (productCount === 0) {
            this.savingsBanner.textContent = 'SELECT A PRODUCT';
            this.savingsBanner.className =
              'w-full py-3 mb-2 text-xs font-medium text-center text-white uppercase bg-black';
          } else {
            let discountPercent;
            if (productCount === 1) discountPercent = '0';
            else if (productCount === 2) discountPercent = '10';
            else if (productCount === 3) discountPercent = '15';
            else discountPercent = '20';

            this.savingsBanner.textContent =
              discountPercent === '0'
                ? 'ADD MORE PRODUCTS TO SAVE'
                : `SAVING ${discountPercent}% ON EVERY PRODUCT!`;
            this.savingsBanner.className =
              discountPercent === '0'
                ? 'w-full py-3 text-xs text-center font-medium uppercase bg-black text-white mb-2'
                : 'w-full py-3 text-xs text-center font-medium uppercase bg-brand-secondary-300 text-black mb-2';
          }
        }

        // Update button text and prices
        const submitButton = this.querySelector('[data-bundle-submit]');
        if (submitButton) {
          const hasOneProduct = productCount >= 1;
          const shouldDisableButton = !hasOneProduct || this.hasExistingBundle;

          // Update button disabled state
          submitButton.disabled = shouldDisableButton;
          const { subtotal, total } = this.calculateTotals();

          // Update button text based on product count
          if (productCount < 1) {
            submitButton.textContent = 'BUNDLE AND SAVE';
          } else if (productCount === 1) {
            submitButton.innerHTML = `
                  ADD TO CART -
                  <span class="ml-2">${window.Shopify.formatMoney(total)}</span>
                `;
          } else {
            const { subtotal, total } = this.calculateTotals();
            submitButton.innerHTML = `
                  ADD TO CART -
                  <span class="line-through text-brand-secondary-350">${window.Shopify.formatMoney(subtotal)}</span>
                  <span class="ml-2">${window.Shopify.formatMoney(total)}</span>
                `;
          }
        }

        // Update submit button state
        if (this.addToCartButton) {
          const hasOneProduct = this.selectedProducts.size >= 1;
          const shouldDisableButton = !hasOneProduct || this.hasExistingBundle;

          this.addToCartButton.disabled = shouldDisableButton;

          // Update button text based on state
          if (this.hasExistingBundle) {
            this.addToCartButton.textContent = 'Add to Cart';
          }
        }

        // After updating the progress bar UI, save the state
        this.saveState();
      }

      // Changed from static to instance method
      notifyBundleRemoved() {
        this.enableBundleBuilder();
      }

      disableVariantSelectors(baseProductId, selectedVariantId) {
        // Find all product cards with the same base product ID
        this.productsContainer
          .querySelectorAll(`[data-base-product-id="${baseProductId}"]`)
          .forEach((wrapper) => {
            const card = wrapper.querySelector('card-product');
            const variantSelector = card?.querySelector('variant-select-card');
            if (!variantSelector) return;

            // Disable all variant buttons except the selected one
            variantSelector
              .querySelectorAll('product-variant')
              .forEach((variant) => {
                const variantId = variant.getAttribute('variant-id');
                if (variantId !== selectedVariantId) {
                  variant.classList.add('opacity-50', 'pointer-events-none');
                  // Add data attribute to mark as disabled
                  variant.setAttribute('data-disabled', 'true');
                  // Remove cursor-pointer class
                  variant.classList.remove('cursor-pointer');
                }
              });
          });
      }

      enableVariantSelectors(baseProductId) {
        // Find all product cards with the same base product ID
        this.productsContainer
          .querySelectorAll(`[data-base-product-id="${baseProductId}"]`)
          .forEach((wrapper) => {
            const card = wrapper.querySelector('card-product');
            const variantSelector = card?.querySelector('variant-select-card');
            if (!variantSelector) return;

            // Enable all variant buttons
            variantSelector
              .querySelectorAll('product-variant')
              .forEach((variant) => {
                variant.classList.remove('opacity-50', 'pointer-events-none');
                // Remove disabled data attribute
                variant.removeAttribute('data-disabled');
                // Restore cursor-pointer class
                variant.classList.add('cursor-pointer');
              });
          });
      }

      updateProductCardStates() {
        const isAtLimit = this.selectedProducts.size >= 5;

        // Get all product cards that are not selected
        this.productsContainer
          .querySelectorAll('.product-card-wrapper')
          .forEach((wrapper) => {
            const card = wrapper.querySelector('card-product');
            if (!card || card.classList.contains('is-selected')) return;

            const productForm = card.querySelector('product-form');
            const submitButton = card.querySelector('button[type="submit"]');

            if (isAtLimit) {
              // Add disabled state to cards
              card.classList.add('at-bundle-limit');

              // Create overlay if it doesn't exist
              if (!card.querySelector('.bundle-limit-overlay')) {
                const overlay = document.createElement('div');
                overlay.className =
                  'absolute inset-0 z-10 flex items-center justify-center p-4 bg-white pointer-events-none bg-opacity-90 bundle-limit-overlay';
                overlay.innerHTML = `
                <div class="text-center bg-white p-4 rounded-md shadow-md max-w-[200px]">
                  <p class="mb-1 text-sm font-semibold text-black">Maximum 5 products</p>
                  <p class="text-xs text-gray-700">Remove a product to add a different one</p>
                </div>
              `;

                // Find the parent element that contains the product image
                const mediaContainer =
                  card.querySelector('[data-product-info-trigger]') ||
                  card.querySelector('a.relative.w-full');
                if (mediaContainer) {
                  mediaContainer.style.position = 'relative';
                  mediaContainer.appendChild(overlay);
                }
              }

              // Disable the button
              if (submitButton) {
                submitButton.disabled = true;
                submitButton.classList.add('opacity-50', 'cursor-not-allowed');
              }

              // Disable variant selectors
              const variantSelector = card.querySelector('variant-select-card');
              if (variantSelector) {
                variantSelector
                  .querySelectorAll('product-variant')
                  .forEach((variant) => {
                    variant.classList.add('opacity-50', 'pointer-events-none');
                    variant.setAttribute('data-disabled-at-limit', 'true');
                    variant.classList.remove('cursor-pointer');
                  });
              }
            } else {
              // Remove disabled state from cards
              card.classList.remove('at-bundle-limit');

              // Remove overlay
              const overlay = card.querySelector('.bundle-limit-overlay');
              if (overlay) {
                overlay.remove();
              }

              // Enable the button
              if (submitButton) {
                submitButton.disabled = false;
                submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
              }

              // Re-enable variant selectors (only if not disabled for other reasons)
              const variantSelector = card.querySelector('variant-select-card');
              if (variantSelector) {
                variantSelector
                  .querySelectorAll('product-variant')
                  .forEach((variant) => {
                    // Only re-enable if it was disabled due to the limit (not due to product selection)
                    if (variant.getAttribute('data-disabled-at-limit') === 'true') {
                      variant.classList.remove('opacity-50', 'pointer-events-none');
                      variant.removeAttribute('data-disabled-at-limit');
                      // Only restore cursor-pointer if not disabled for other reasons
                      if (!variant.hasAttribute('data-disabled')) {
                        variant.classList.add('cursor-pointer');
                      }
                    }
                  });
              }
            }
          });
      }

      async checkForExistingDiscounts() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();

          console.group('Bundle Builder Discount Debug');
          console.log('Cart data:', cart);

          // Check for any cart-level discounts that aren't bundle builder discounts
          const hasNonBundleCartDiscount =
            cart.cart_level_discount_applications?.some(
              (discount) => !discount.title.toLowerCase().includes('bundle builder')
            );

          // Check for any line item discounts that aren't bundle builder discounts
          const hasNonBundleLineDiscount = cart.items?.some((item) =>
            item.line_level_discount_allocations?.some(
              (allocation) =>
                !allocation.discount_application.title
                  .toLowerCase()
                  .includes('bundle builder')
            )
          );

          const hasBundleLineDiscount = cart.items?.some((item) =>
            item.line_level_discount_allocations?.some((allocation) =>
              allocation.discount_application.title
                .toLowerCase()
                .includes('bundle builder')
            )
          );

          console.log(hasBundleLineDiscount, 'has bundle line discount!!');

          const hasNonBundleDiscount =
            hasNonBundleCartDiscount || hasNonBundleLineDiscount;
          console.log('Has non-bundle cart discount:', hasNonBundleCartDiscount);
          console.log('Has non-bundle line discount:', hasNonBundleLineDiscount);
          console.log('Has any non-bundle discount:', hasNonBundleDiscount);
          console.groupEnd();

          return hasNonBundleDiscount;
        } catch (error) {
          console.error('Error checking for discounts:', error);
          return false;
        }
      }

      showDiscountWarning() {
        // You'll need to add this element to your HTML template
        const warningElement = this.querySelector('[data-discount-warning]');
        if (warningElement) {
          warningElement.classList.remove('hidden');
        } else {
          // Create warning if it doesn't exist
          const warning = document.createElement('div');
          warning.setAttribute('data-discount-warning', '');
          warning.classList.add(
            'text-warning-500',
            'p-4',
            'mb-4',
            'bg-warning-50',
            'rounded'
          );
          warning.textContent =
            'Note: Bundle discounts cannot be combined with other offers.';

          // Insert at the top of the bundle builder
          this.insertAdjacentElement('afterbegin', warning);
        }
      }
    }
  );
}
